/**
 * Conteúdo estruturado dos modais de ajuda do painel Pipeline KB.
 * Cada tópico descreve regras, colunas e cálculos daquela seção.
 * Renderizador vive em KbHelpButton.tsx.
 */
import type { LucideIcon } from "lucide-react";
import {
  Database,
  Layers,
  Building2,
  Skull,
  FileText,
  Gauge,
} from "lucide-react";

export type KbHelpTopic =
  | "root"
  | "overview"
  | "tenants"
  | "dlq"
  | "page"
  | "pageDetail";

export type HelpSection =
  | { kind: "paragraph"; text: string }
  | { kind: "list"; items: string[] }
  | { kind: "table"; columns: string[]; rows: string[][] }
  | { kind: "code"; label?: string; code: string }
  | {
      kind: "callout";
      tone: "info" | "warning" | "success";
      title: string;
      body: string;
    }
  | { kind: "heading"; text: string };

export interface HelpDoc {
  title: string;
  subtitle: string;
  icon: LucideIcon;
  sections: HelpSection[];
}

// ---------------------------------------------------------------------------
// Conteúdo
// ---------------------------------------------------------------------------

export const KB_HELP_CONTENT: Record<KbHelpTopic, HelpDoc> = {
  // -------------------------------------------------------------------------
  root: {
    title: "Pipeline KB — visão da página",
    subtitle:
      "Monitor operacional das filas que indexam a Base de Conhecimento (KB) em toda a plataforma.",
    icon: Gauge,
    sections: [
      { kind: "heading", text: "O que este painel mostra" },
      {
        kind: "paragraph",
        text: "A Base de Conhecimento é indexada em duas etapas encadeadas: chunkify (quebra o conteúdo da página em pedaços) e embed_primary (gera o embedding vetorial de cada chunk e grava na tabela poliglota da dimensão vigente). Este painel expõe o estado dessas filas por queue, por scope, por tenant e por página.",
      },
      { kind: "heading", text: "Tabelas físicas envolvidas" },
      {
        kind: "table",
        columns: ["Tabela", "Papel"],
        rows: [
          ["kb_pages", "Página lógica de conteúdo (título, status, space)."],
          ["kb_chunks", "Pedaços de texto derivados da página."],
          [
            "kb_chunk_embeddings_{768,1024,1536,3072}",
            "Vetores por dimensão. Pipeline atual grava em 1536; outras existem por reindex histórico.",
          ],
          [
            "kb_embedding_jobs",
            "Fila principal (scopes chunkify e embed_primary).",
          ],
          [
            "kb_transcription_jobs / kb_import_jobs / kb_reindex_jobs",
            "Filas auxiliares (mídia, importação e reindex).",
          ],
          [
            "kb_job_dlq",
            "Dead-letter queue: jobs que estouraram max_attempts ou falharam de forma fatal.",
          ],
        ],
      },
      { kind: "heading", text: "Fonte dos dados" },
      {
        kind: "paragraph",
        text: "Todo dado é agregado server-side por RPCs SECURITY DEFINER com prefixo kb_admin_pipeline_*. O acesso é restrito a root admin via kb_admin_assert_root; tentativas negadas são registradas em security_bypass_attempts.",
      },
      {
        kind: "code",
        label: "RPCs consumidas por esta página",
        code: `kb_admin_pipeline_overview()
kb_admin_pipeline_volume_24h(p_bucket_minutes)
kb_admin_pipeline_by_tenant(p_search, p_page, p_page_size)
kb_admin_pipeline_by_page(p_tenant_id, p_search, p_status, p_page, p_page_size)
kb_admin_pipeline_page_detail(p_page_id)
kb_admin_pipeline_dlq_list(p_search, p_page, p_page_size)
kb_admin_pipeline_reprocess_dlq(p_dlq_id)
kb_admin_pipeline_purge_failed(p_queue, p_older_than_hours)`,
      },
      { kind: "heading", text: "Atualização" },
      {
        kind: "list",
        items: [
          "Polling automático via React Query (intervalo ~15s).",
          "Botão de refresh manual no cabeçalho (invalida a query key ‘kb-admin’).",
          "Ações de escrita (reprocessar DLQ, purge failed) invalidam as queries afetadas.",
        ],
      },
      { kind: "heading", text: "Glossário" },
      {
        kind: "table",
        columns: ["Termo", "Significado"],
        rows: [
          ["chunk", "Fragmento textual indexável derivado de uma página."],
          [
            "embedding",
            "Vetor numérico gerado a partir de um chunk, usado em busca semântica/RAG.",
          ],
          [
            "scope",
            "Sub-tipo do job em kb_embedding_jobs (chunkify | embed_primary | ...).",
          ],
          [
            "dimensão",
            "Tamanho do vetor de embedding (768/1024/1536/3072). Cada dimensão vive em uma tabela dedicada.",
          ],
          [
            "DLQ",
            "Dead-letter queue: destino final de jobs irrecuperáveis; requer reprocesso manual.",
          ],
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  overview: {
    title: "Aba Visão geral",
    subtitle:
      "Estado agregado das filas KB, breakdown por scope e volume de jobs nas últimas 24 horas.",
    icon: Layers,
    sections: [
      { kind: "heading", text: "KPIs do topo" },
      {
        kind: "paragraph",
        text: "Somam todas as filas monitoradas (rows com scope = __total__ retornadas por kb_admin_pipeline_overview). Refletem o instantâneo atual da coluna status de cada tabela de jobs.",
      },
      {
        kind: "table",
        columns: ["KPI", "Regra"],
        rows: [
          ["Pendentes", "COUNT(*) WHERE status = 'pending' em todas as filas."],
          ["Processando", "COUNT(*) WHERE status = 'processing'."],
          [
            "Falhos",
            "COUNT(*) WHERE status = 'failed'. Ainda vivos na fila — podem ir para DLQ se estourarem tentativas.",
          ],
          ["DLQ", "COUNT(*) em kb_job_dlq por fila."],
        ],
      },
      { kind: "heading", text: "Tabela “Filas”" },
      {
        kind: "paragraph",
        text: "Uma linha por fila física. A ação da lixeira executa kb_admin_pipeline_purge_failed(queue, olderThanHours=168) — remove jobs em status failed com mais de 7 dias, útil após um incidente já reprocessado.",
      },
      {
        kind: "list",
        items: [
          "kb_embedding_jobs — fila principal (chunkify + embed_primary).",
          "kb_transcription_jobs — transcrição de mídia (áudio/vídeo).",
          "kb_import_jobs — importação em lote (URLs, docs).",
          "kb_reindex_jobs — reprocessamento massivo por dimensão/tenant.",
        ],
      },
      { kind: "heading", text: "Tabela “kb_embedding_jobs — por scope”" },
      {
        kind: "paragraph",
        text: "Mesmo contador de status, quebrado por scope. Serve para diagnosticar em qual etapa a fila está travada: chunkify pendente indica página nova ainda não fragmentada; embed_primary pendente indica chunks prontos aguardando embedding.",
      },
      { kind: "heading", text: "Gráfico “Volume 24h”" },
      {
        kind: "paragraph",
        text: "Área empilhada dos jobs criados por scope, bucket de 1h, últimas 24h. Fonte: kb_admin_pipeline_volume_24h(60). Ausência de pontos = nenhum job criado no período.",
      },
      {
        kind: "callout",
        tone: "info",
        title: "Como ler a saúde da fila",
        body: "Falhos > 0 é aceitável em regime transitório (retry ativo). Falhos crescendo sem redução de Pendentes indica erro sistemático — verifique a coluna last_error na aba Por tenant → página → Últimos jobs.",
      },
    ],
  },

  // -------------------------------------------------------------------------
  tenants: {
    title: "Aba Por tenant",
    subtitle:
      "Agregação do pipeline por tenant, com drill-down para páginas e detalhe individual.",
    icon: Building2,
    sections: [
      { kind: "heading", text: "Fonte" },
      {
        kind: "code",
        code: "kb_admin_pipeline_by_tenant(p_search, p_page, p_page_size)",
      },
      {
        kind: "paragraph",
        text: "Retorna uma linha por tenant que possua kb_pages, com contadores agregados. Busca é ILIKE server-side sobre o nome do tenant.",
      },
      { kind: "heading", text: "Colunas" },
      {
        kind: "table",
        columns: ["Coluna", "Significado"],
        rows: [
          [
            "Tenant",
            "Nome do tenant + primeiros 8 chars do UUID (padrão de exibição).",
          ],
          [
            "Páginas",
            "COUNT(*) em kb_pages WHERE status = 'published' AND tenant_id = t.id.",
          ],
          [
            "Chunks",
            "COUNT(*) em kb_chunks do tenant. Zero indica que o chunkify ainda não rodou (ou falhou).",
          ],
          [
            "Embeddings",
            "Soma das 4 tabelas kb_chunk_embeddings_{768,1024,1536,3072} filtradas por tenant_id.",
          ],
          [
            "Pend. / Proc. / Falhos",
            "COUNT em kb_embedding_jobs por status, escopo = tenant.",
          ],
          [
            "DLQ",
            "COUNT em kb_job_dlq do tenant (todos os scopes/filas KB).",
          ],
        ],
      },
      { kind: "heading", text: "Interação" },
      {
        kind: "list",
        items: [
          "Clique na linha → abre o Sheet “Páginas do tenant”.",
          "Chevron à direita é indicativo visual; toda a linha é clicável.",
          "Paginação server-side (25 por página); total vem em total_count.",
        ],
      },
      {
        kind: "callout",
        tone: "warning",
        title: "Discrepância Chunks × Embeddings",
        body: "Chunks > Embeddings é normal durante processamento (embed_primary pendente). Chunks > Embeddings com Pend. = 0 e Falhos > 0 indica embeddings perdidos — abra a página, verifique Últimos jobs e reprocesse via DLQ se necessário.",
      },
    ],
  },

  // -------------------------------------------------------------------------
  dlq: {
    title: "Aba DLQ (Dead-Letter Queue)",
    subtitle:
      "Jobs irrecuperáveis que exigem intervenção manual — origem, motivo e reprocesso.",
    icon: Skull,
    sections: [
      { kind: "heading", text: "O que é DLQ" },
      {
        kind: "paragraph",
        text: "Tabela kb_job_dlq. Um job é movido para a DLQ quando (a) ultrapassa max_attempts com falha persistente, ou (b) o worker classifica o erro como fatal (payload malformado, referência inexistente, política de segurança violada).",
      },
      { kind: "heading", text: "Colunas" },
      {
        kind: "table",
        columns: ["Coluna", "Descrição"],
        rows: [
          [
            "Fila / Scope",
            "queue_name original (ex.: kb_embedding_jobs) e scope (ex.: chunkify).",
          ],
          ["Tenant", "tenant_id resolvido do payload."],
          [
            "Página",
            "payload->>'page_id'. Se vazio, o job não estava vinculado a uma página específica.",
          ],
          [
            "Motivo (reason)",
            "Categoria da falha (ex.: max_attempts_exceeded, fatal_error).",
          ],
          ["Erro (last_error)", "Última mensagem de erro do worker."],
          [
            "Tent.",
            "Tentativas consumidas antes da queda (attempts).",
          ],
          [
            "Movido em (moved_at)",
            "Timestamp em que o job foi enviado para a DLQ.",
          ],
        ],
      },
      { kind: "heading", text: "Ação: Reprocessar" },
      {
        kind: "code",
        code: "kb_admin_pipeline_reprocess_dlq(p_dlq_id UUID)",
      },
      {
        kind: "paragraph",
        text: "Recria o job na fila original com status='pending' e attempts=0, mantendo o payload. Marca reprocessed_at no registro da DLQ para evitar duplicação. O botão fica desabilitado se o item já foi reprocessado.",
      },
      {
        kind: "callout",
        tone: "warning",
        title: "Antes de reprocessar em massa",
        body: "Confirme que a causa raiz foi corrigida (chave de API renovada, quota liberada, migração aplicada). Reprocessar sem corrigir apenas devolve os jobs à DLQ e polui o log.",
      },
    ],
  },

  // -------------------------------------------------------------------------
  page: {
    title: "Sheet — Páginas do tenant",
    subtitle:
      "Uma linha por página do tenant selecionado, com contadores de indexação e último erro.",
    icon: FileText,
    sections: [
      { kind: "heading", text: "Fonte" },
      {
        kind: "code",
        code: "kb_admin_pipeline_by_page(p_tenant_id, p_search, p_status, p_page, p_page_size)",
      },
      { kind: "heading", text: "Filtros" },
      {
        kind: "list",
        items: [
          "Busca (search): ILIKE server-side sobre o título da página.",
          "Status: draft, in_review, published, archived (kb_pages.status).",
          "Ambos aplicados no server; paginação de 25/pág.",
        ],
      },
      { kind: "heading", text: "Colunas" },
      {
        kind: "table",
        columns: ["Coluna", "Cálculo"],
        rows: [
          [
            "Página",
            "Título + space + primeiros 8 do UUID. Clique abre o detalhe.",
          ],
          ["Status", "kb_pages.status."],
          [
            "Chunks",
            "COUNT(*) em kb_chunks WHERE page_id = p.id.",
          ],
          [
            "Emb.",
            "Soma das 4 dimensões via JOIN kb_chunks c ON c.id = e.chunk_id WHERE c.page_id = p.id. Compare com Chunks para saber o quanto está indexado.",
          ],
          [
            "Pend.",
            "COUNT em kb_embedding_jobs WHERE page_id = p.id AND status = 'pending'.",
          ],
          [
            "Falhos",
            "COUNT em kb_embedding_jobs WHERE page_id = p.id AND status = 'failed'.",
          ],
          [
            "Erro",
            "last_error do job mais recente da página (qualquer scope). Truncado; tooltip mostra completo.",
          ],
        ],
      },
      {
        kind: "callout",
        tone: "info",
        title: "Página com Chunks=0",
        body: "Significa que chunkify ainda não completou (verifique Pend./Falhos) OU a página está sem conteúdo indexável (kb_page_blocks vazio).",
      },
    ],
  },

  // -------------------------------------------------------------------------
  pageDetail: {
    title: "Sheet — Detalhe da página",
    subtitle:
      "Diagnóstico completo de uma única página: KPIs, embeddings por dimensão, últimos jobs e DLQ.",
    icon: Database,
    sections: [
      { kind: "heading", text: "Fonte" },
      {
        kind: "code",
        code: "kb_admin_pipeline_page_detail(p_page_id UUID)",
      },
      {
        kind: "paragraph",
        text: "Retorna JSONB único com { page, jobs_summary, chunks_count, embeddings_by_dim, recent_jobs, dlq_items }.",
      },
      { kind: "heading", text: "KPIs (jobs_summary)" },
      {
        kind: "table",
        columns: ["KPI", "Regra"],
        rows: [
          [
            "Pend.",
            "COUNT em kb_embedding_jobs WHERE page_id = p.id AND status = 'pending'.",
          ],
          ["Proc.", "Mesmo, status = 'processing'."],
          [
            "Falhos",
            "Mesmo, status = 'failed'. Ainda na fila principal; podem ir para DLQ.",
          ],
          [
            "Concluídos",
            "Mesmo, status = 'done'. Contador histórico dos jobs bem-sucedidos.",
          ],
        ],
      },
      { kind: "heading", text: "Chunks e Embeddings por dimensão" },
      {
        kind: "paragraph",
        text: "Chunks: N mostra COUNT(*) em kb_chunks da página. O grid abaixo conta embeddings existentes em cada tabela poliglota (768/1024/1536/3072), via JOIN kb_chunks para amarrar pelo page_id.",
      },
      {
        kind: "callout",
        tone: "info",
        title: "Dimensão vigente",
        body: "O pipeline atual usa 1536. Valores em outras dimensões só aparecem quando houve reindex histórico (kb_reindex_jobs) ou quando o tenant está usando um provedor de embedding alternativo.",
      },
      { kind: "heading", text: "Últimos jobs (20 mais recentes)" },
      {
        kind: "table",
        columns: ["Coluna", "Descrição"],
        rows: [
          ["Scope", "chunkify | embed_primary | ..."],
          ["Status", "pending | processing | done | failed."],
          [
            "Tent.",
            "attempts/max_attempts. Ao atingir max_attempts, o job vai para DLQ.",
          ],
          [
            "Atualizado",
            "updated_at relativo (‘há X minutos’). Congelado + status=processing = worker travado.",
          ],
          [
            "Erro",
            "last_error. Vazio para jobs bem-sucedidos ou ainda em execução.",
          ],
        ],
      },
      { kind: "heading", text: "DLQ desta página" },
      {
        kind: "paragraph",
        text: "Itens de kb_job_dlq com payload->>'page_id' = p.id. O botão Reprocessar chama kb_admin_pipeline_reprocess_dlq(dlq_id); fica desabilitado se reprocessed_at ≠ NULL.",
      },
      {
        kind: "callout",
        tone: "success",
        title: "Roteiro de troubleshooting",
        body: "1) KPI Falhos > 0 → abra Últimos jobs, leia last_error. 2) Se attempts = max_attempts e status = failed → o próximo retry moverá para DLQ. 3) Se já em DLQ, corrija a causa e clique Reprocessar. 4) Confirme Chunks == Emb. 1536 ao final para garantir indexação completa.",
      },
    ],
  },
};
